import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import api from "@/lib/api";
import { ArrowLeft, ScanLine, Keyboard, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Scanner() {
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const [starting, setStarting] = useState(true);
  const [camError, setCamError] = useState(false);
  const [manual, setManual] = useState("");
  const handledRef = useRef(false);

  const resolveNumero = async (numero) => {
    if (handledRef.current) return;
    handledRef.current = true;
    try {
      const res = await api.get(`/bikes/by-number/${encodeURIComponent(numero)}`);
      navigate(`/bici/${res.data.id}`);
    } catch (e) {
      toast.error(`Nessuna bici con numero "${numero}"`);
      handledRef.current = false;
    }
  };

  const onDecode = (text) => {
    let numero = text.trim();
    const m = text.match(/\/b\/([^/?#]+)/);
    if (m) numero = decodeURIComponent(m[1]);
    resolveNumero(numero);
  };

  const startedRef = useRef(false);

  useEffect(() => {
    const html5 = new Html5Qrcode("qr-reader");
    scannerRef.current = html5;
    let active = true;
    html5.start(
      { facingMode: "environment" },
      { fps: 10, qrbox: { width: 220, height: 220 } },
      (decoded) => { if (active) { onDecode(decoded); } },
      () => {}
    ).then(() => { startedRef.current = true; setStarting(false); })
      .catch(() => { setStarting(false); setCamError(true); toast.error("Fotocamera non disponibile. Usa l'inserimento manuale."); });

    return () => {
      active = false;
      const inst = scannerRef.current;
      if (inst && startedRef.current) {
        try {
          inst.stop().then(() => { try { inst.clear(); } catch (e) {} }).catch(() => {});
        } catch (e) {}
      }
    };
    // eslint-disable-next-line
  }, []);

  const goManual = (e) => {
    e.preventDefault();
    if (manual.trim()) resolveNumero(manual.trim());
  };

  return (
    <div className="p-5 sm:p-8 max-w-xl mx-auto fade-in">
      <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-5"><ArrowLeft className="h-4 w-4" /> Dashboard</button>
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Apri scheda inquadrando il QR</div>
        <h1 className="font-display text-4xl tracking-tight flex items-center gap-2"><ScanLine className="h-8 w-8 text-[#f98125]" /> SCANNER QR</h1>
      </div>

      <div className="bg-[#141414] border border-zinc-800 rounded-sm overflow-hidden mb-6">
        <div id="qr-reader" className="w-full [&_video]:w-full [&_video]:object-cover" />
        {starting && <div className="py-12 flex flex-col items-center gap-3 text-zinc-500"><Loader2 className="h-6 w-6 animate-spin" /> Avvio fotocamera...</div>}
        {camError && <div className="py-12 flex flex-col items-center gap-2 text-zinc-500 text-sm"><ScanLine className="h-7 w-7 text-zinc-700" /> Fotocamera non disponibile. Inserisci il numero qui sotto.</div>}
      </div>

      <form onSubmit={goManual} className="bg-[#141414] border border-zinc-800 rounded-sm p-4">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3 flex items-center gap-2"><Keyboard className="h-4 w-4" /> Inserimento manuale</div>
        <div className="flex gap-2">
          <Input value={manual} onChange={(e) => setManual(e.target.value)} data-testid="scanner-manual-input"
            placeholder="Numero bici (es. 12)" className="bg-[#0A0A0A] border-zinc-800 rounded-sm" />
          <button type="submit" data-testid="scanner-manual-go" className="rounded-sm bg-[#f98125] text-white font-bold px-5 hover:bg-[#d96a12]">Vai</button>
        </div>
      </form>
    </div>
  );
}
